import { getLegalMoves, moveNotation, positionKey, type GameState, type Move } from "../game";
import { getDifficultyPolicy, type BotDifficulty } from "./difficulty";
import { selectBotFallbackMove } from "./fallback";
import {
  BOT_PROTOCOL_VERSION,
  parseBotWorkerResponse,
  type BotSearchRequest,
  type BotWorkerRequest,
} from "./protocol";

export type BotAuthority = Readonly<{
  matchId: string;
  revision: number;
  state: GameState;
  positionKeys?: readonly string[];
}>;

export type BotTurnRequest = BotAuthority &
  Readonly<{
    difficulty: BotDifficulty;
    seed: number;
    readAuthority: () => BotAuthority;
  }>;

export type BotFallbackReason =
  | "timeout"
  | "worker-error"
  | "message-error"
  | "post-failed"
  | "malformed-response"
  | "stale-response"
  | "worker-reported-error"
  | "illegal-response"
  | "compatibility-rules";

export type BotDecision = Readonly<{
  move: Move;
  source: "search" | "fallback";
  fallbackReason?: BotFallbackReason;
  nodes?: number;
  completedDepth?: number;
}>;

export type BotWorkerHandlers = Readonly<{
  message: (value: unknown) => void;
  error: () => void;
  messageError: () => void;
}>;

export type BotWorkerPort = Readonly<{
  postMessage(message: BotWorkerRequest): void;
  setHandlers(handlers: BotWorkerHandlers): void;
  terminate(): void;
}>;

export type BotWorkerFactory = () => BotWorkerPort;

export type BotTimer = Readonly<{
  set(callback: () => void, delayMs: number): unknown;
  clear(handle: unknown): void;
}>;

type PendingRequest = {
  readonly request: BotTurnRequest;
  readonly resolve: (decision: BotDecision) => void;
  readonly reject: (error: Error) => void;
  timeoutHandle: unknown;
};

export class BotRequestCancelledError extends Error {
  readonly reason: "cancelled" | "obsolete" | "disposed";

  constructor(reason: "cancelled" | "obsolete" | "disposed") {
    super(
      reason === "obsolete"
        ? "The bot request no longer matches the authoritative match revision."
        : `The bot request was ${reason}.`,
    );
    this.name = "BotRequestCancelledError";
    this.reason = reason;
  }
}

function defaultTimer(): BotTimer {
  return Object.freeze({
    set: (callback: () => void, delayMs: number): number =>
      globalThis.setTimeout(callback, delayMs),
    clear: (handle: unknown): void => globalThis.clearTimeout(handle as number),
  });
}

function sameIdentity(left: BotAuthority, right: BotAuthority): boolean {
  return left.matchId === right.matchId && left.revision === right.revision;
}

function legalMoveFrom(state: GameState, move: Move): Move | null {
  const notation = moveNotation(move);
  return getLegalMoves(state).find((candidate) => moveNotation(candidate) === notation) ?? null;
}

function safelyTerminate(worker: BotWorkerPort | null): void {
  try {
    worker?.terminate();
  } catch {
    // A broken adapter must not prevent canonical fallback or later recovery.
  }
}

/**
 * Owns a single replaceable worker. Search faults never leak an untrusted move:
 * the decision is checked against a fresh authoritative state snapshot first.
 */
export class BotClient {
  readonly #factory: BotWorkerFactory;
  readonly #timer: BotTimer;
  #worker: BotWorkerPort | null;
  #pending: PendingRequest | null = null;
  #generation = 0;
  #disposed = false;

  constructor(factory: BotWorkerFactory, timer: BotTimer = defaultTimer()) {
    this.#factory = factory;
    this.#timer = timer;
    this.#worker = this.#createWorker();
  }

  requestMove(request: BotTurnRequest): Promise<BotDecision> {
    if (this.#disposed) return Promise.reject(new BotRequestCancelledError("disposed"));
    if (this.#pending !== null) this.cancel();

    const legalMoves = getLegalMoves(request.state);
    if (legalMoves.length === 0) {
      return Promise.reject(new Error("A bot move was requested from a terminal state."));
    }

    const policy = getDifficultyPolicy(request.difficulty);
    const message: BotSearchRequest = Object.freeze({
      type: "search",
      protocolVersion: BOT_PROTOCOL_VERSION,
      matchId: request.matchId,
      revision: request.revision,
      state: request.state,
      positionKeys: request.positionKeys ?? Object.freeze([positionKey(request.state)]),
      difficulty: request.difficulty,
      seed: request.seed,
      budget: policy.budget,
    });

    return new Promise<BotDecision>((resolve, reject) => {
      const pending: PendingRequest = {
        request,
        resolve,
        reject,
        timeoutHandle: undefined,
      };
      this.#pending = pending;
      pending.timeoutHandle = this.#timer.set(
        () => this.#recoverWithFallback(pending, "timeout"),
        policy.responseCeilingMs,
      );

      try {
        if (this.#worker === null) throw new Error("Bot worker is unavailable.");
        this.#worker.postMessage(message);
      } catch {
        this.#recoverWithFallback(pending, "post-failed");
      }
    });
  }

  cancel(): void {
    const pending = this.#pending;
    if (pending !== null) {
      this.#timer.clear(pending.timeoutHandle);
      this.#pending = null;
      pending.reject(new BotRequestCancelledError("cancelled"));
    }
    if (!this.#disposed) this.#replaceWorker();
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    const pending = this.#pending;
    if (pending !== null) {
      this.#timer.clear(pending.timeoutHandle);
      this.#pending = null;
      pending.reject(new BotRequestCancelledError("disposed"));
    }
    safelyTerminate(this.#worker);
  }

  #createWorker(): BotWorkerPort | null {
    const generation = ++this.#generation;
    let worker: BotWorkerPort;
    try {
      worker = this.#factory();
    } catch {
      return null;
    }
    try {
      worker.setHandlers({
        message: (value) => {
          if (generation === this.#generation) this.#handleMessage(value);
        },
        error: () => {
          if (generation === this.#generation) this.#handleFault("worker-error");
        },
        messageError: () => {
          if (generation === this.#generation) this.#handleFault("message-error");
        },
      });
    } catch {
      safelyTerminate(worker);
      return null;
    }
    return worker;
  }

  #replaceWorker(): void {
    safelyTerminate(this.#worker);
    this.#worker = this.#createWorker();
  }

  #handleMessage(value: unknown): void {
    const pending = this.#pending;
    if (pending === null) return;

    const parsed = parseBotWorkerResponse(value);
    if (!parsed.ok) {
      this.#recoverWithFallback(pending, "malformed-response");
      return;
    }

    const response = parsed.value;
    if (
      response.matchId !== pending.request.matchId ||
      response.revision !== pending.request.revision
    ) {
      this.#recoverWithFallback(pending, "stale-response");
      return;
    }
    if (response.type === "error") {
      this.#recoverWithFallback(pending, "worker-reported-error");
      return;
    }
    const budget = getDifficultyPolicy(pending.request.difficulty).budget;
    if (response.nodes > budget.nodeLimit || response.completedDepth > budget.maxDepth) {
      this.#recoverWithFallback(pending, "malformed-response");
      return;
    }

    const authority = pending.request.readAuthority();
    if (!sameIdentity(pending.request, authority)) {
      this.#rejectObsolete(pending);
      return;
    }
    const move = legalMoveFrom(authority.state, response.move);
    if (move === null) {
      this.#recoverWithFallback(pending, "illegal-response");
      return;
    }

    this.#timer.clear(pending.timeoutHandle);
    this.#pending = null;
    pending.resolve(
      Object.freeze({
        move,
        source: "search",
        nodes: response.nodes,
        completedDepth: response.completedDepth,
      }),
    );
  }

  #handleFault(reason: "worker-error" | "message-error"): void {
    const pending = this.#pending;
    if (pending !== null) this.#recoverWithFallback(pending, reason);
    else if (!this.#disposed) this.#replaceWorker();
  }

  #recoverWithFallback(pending: PendingRequest, reason: BotFallbackReason): void {
    if (this.#pending !== pending) return;
    const authority = pending.request.readAuthority();
    if (!sameIdentity(pending.request, authority)) {
      this.#rejectObsolete(pending);
      return;
    }

    const fallback = selectBotFallbackMove(authority.state);
    this.#timer.clear(pending.timeoutHandle);
    this.#pending = null;
    if (!this.#disposed) this.#replaceWorker();
    if (fallback === null) {
      pending.reject(new Error("No authoritative legal fallback move is available."));
      return;
    }
    pending.resolve(Object.freeze({ move: fallback, source: "fallback", fallbackReason: reason }));
  }

  #rejectObsolete(pending: PendingRequest): void {
    if (this.#pending !== pending) return;
    this.#timer.clear(pending.timeoutHandle);
    this.#pending = null;
    if (!this.#disposed) this.#replaceWorker();
    pending.reject(new BotRequestCancelledError("obsolete"));
  }
}
